import type { OutlineSection, SectionMeta, SectionStatus } from './prototype2Data';

const stripSectionNumber = (title: string): string => title.replace(/^\d+\.\s*/, '').trim();

const slugify = (text: string): string => {
  const slug = stripSectionNumber(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return slug.slice(0, 48) || 'section';
};

const bulletImpliesChart = (bullet: string): boolean =>
  /\d|%|rate|rates|data|chart|trend|enrollment|completion|participation/i.test(bullet);

const buildSectionIntro = (title: string, bullets: string[]): string => {
  if (bullets.length === 0) return `This section covers ${title}.`;
  if (bullets.length === 1) return `This section examines ${bullets[0].charAt(0).toLowerCase()}${bullets[0].slice(1)}.`;
  return `This section covers ${title.toLowerCase()}, including ${bullets.join('; ')}.`;
};

const outlineToReportSections = (outline: OutlineSection[]): SectionMeta[] => {
  const usedIds = new Set<string>();

  const uniqueSectionId = (base: string): string => {
    let id = base;
    let suffix = 2;
    while (usedIds.has(id)) {
      id = `${base}-${suffix}`;
      suffix += 1;
    }
    usedIds.add(id);
    return id;
  };

  return outline.map((item, index) => {
    const title = stripSectionNumber(item.title);
    const sectionId = uniqueSectionId(slugify(item.title));
    const bullets = item.bullets.map((b) => b.trim()).filter(Boolean);
    const paragraphSources = bullets.length > 0 ? bullets : [title];
    const status: SectionStatus = index === 0 ? 'in-progress' : 'not-started';

    return {
      id: sectionId,
      title,
      index: index + 1,
      description: paragraphSources[0],
      intro: buildSectionIntro(title, bullets),
      status,
      paragraphs: paragraphSources.map((bullet, paragraphIndex) => ({
        id: `${sectionId}__p${paragraphIndex + 1}`,
        num: paragraphIndex + 1,
        title: bullet,
        hasChart: paragraphIndex === 0 && bulletImpliesChart(bullet),
      })),
    };
  });
};

export { outlineToReportSections, stripSectionNumber };
